import React, { useCallback, useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useAppContext } from '@/components/AppContext'
import withDefaultLayout from '@/hoc/WithDefaultLayout'
import AdminBreadcrumb from '@/ui/organisms/AdminBreadcrumb'
import TempleListPage from './components/TempleListPage'
import { STORE_SERVICE_TYPE, Store, Stores, TempleData } from '@a4b/api/src/modules/Monetisation/Offerings/types'
import Error from '@/components/Error'

interface Props { }

const getPageBreadcrumbs = () => [
  {
    path: `/monetisation/offerings`,
    breadcrumbName: 'Hub List',
  },
  {
    path: '/monetisation/offerings/home/hub',
    breadcrumbName: 'Temple List',
  },
]

interface FiltersType {
  serviceType: STORE_SERVICE_TYPE | undefined
  storeId: string | undefined
  templeId: string | undefined
}

const TempleListScreen: React.FC<Props> = () => {
  const { networkInstance, monetisationRoles } = useAppContext()
  const { monetisationOfferingsApi } = networkInstance.clientWithHeaders
  let { hubId } = useParams()

  const [stores, setStores] = React.useState<Stores>()
  const [storesPaginated, setStoresPaginated] = React.useState<Store[]>([])
  const [currentPage, setCurrentPage] = React.useState<number>(0)
  const [pageSize, setPageSize] = React.useState<number>(10)
  const [errorPage, setErrorPage] = React.useState<boolean>(false)
  const [isLoading, setIsLoading] = React.useState<boolean>(false)
  let [searchParams, setSearchParams] = useSearchParams()
  const [templeListing, setTempleListing] = React.useState<boolean>(localStorage.getItem('templeListing') === 'true')
  const [templeList, setTempleList] = useState<TempleData[]>([])
  const [templeListPaginated, setTempleListPaginated] = React.useState<TempleData[]>([])
  const [filters, setFilters] = useState<FiltersType>({
    serviceType: 'offerings',
    storeId: '',
    templeId: ''
  })

  const getHubManagerList = useCallback(async () => {
    try {
      setIsLoading(true)
      if (templeListing) {
        const response = await monetisationOfferingsApi.templesApi.GetTempleList(hubId, filters?.templeId)
        const temples = response?.data?.data?.temples_list
        setTempleList(temples)
        setTempleListPaginated(temples?.slice(0, pageSize))

        setStores(undefined)
        setStoresPaginated([])
      } else {
        const storeListByHubId = await monetisationOfferingsApi.hub.getStoresByHubId(
          hubId || '', filters?.serviceType!, filters?.storeId!
        )


        setStores(storeListByHubId?.data?.data)
        setStoresPaginated(storeListByHubId?.data?.data.stores.slice(0, pageSize))

        setTempleList([])
        setTempleListPaginated([])
      }
      setIsLoading(false)
    } catch (error) {
      setIsLoading(false)
      setErrorPage(true)
    }
  }, [templeListing, monetisationOfferingsApi.templesApi, monetisationOfferingsApi.hub, hubId, filters?.templeId, filters?.serviceType, filters?.storeId, pageSize])

  useEffect(() => {
    getHubManagerList()
  }, [getHubManagerList])

  const paginationHandler = (page: number, pageSize: number) => {
    let derivedPage = page === 0 ? 0 : page - 1
    setCurrentPage(page)
    if (templeListing) {
      let temples = templeList?.slice(derivedPage * pageSize, (derivedPage + 1) * pageSize,) || []
      setTempleListPaginated(temples)
    } else {
      let store = stores?.stores?.slice(derivedPage * pageSize, (derivedPage + 1) * pageSize,) || []
      setStoresPaginated(store)
    }
  }

  const updateFilters = (type: string, value: string) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [type]: value
    }))
  }

  const onToggleTempleListing = (checked: boolean) => {
    localStorage.setItem('templeListing', String(checked))
    setTempleListing(checked)
  }

  return (
    <div style={{ padding: '0 40px' }}>
      {errorPage ?
        <Error text='Error While fetching Temple List!' /> :
        <>
          <AdminBreadcrumb routes={getPageBreadcrumbs()} />
          <TempleListPage
            hubId={hubId || ''}
            hubName={searchParams.get('name')}
            data={stores}
            paginationOnChange={paginationHandler}
            currentPage={currentPage}
            totalRecords={templeListing ? (templeList?.length || 0) : (stores?.stores.length || 0)}
            dataRecords={storesPaginated}
            showAddNewTemple={monetisationRoles?.user_type === 'admin' || monetisationRoles?.user_type === 'super_admin'}
            pageSize={pageSize}
            updateFilters={updateFilters}
            filters={filters}
            isLoading={isLoading}
            getHubManagersList={getHubManagerList}
            templeListing={templeListing}
            onToggleTempleListing={onToggleTempleListing}
            templeListPaginated={templeListPaginated}
          />
        </>
      }
    </div>
  )
}

export default withDefaultLayout(TempleListScreen)
